import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ApiOrder, calculateOrderPayment, createOrder, getOrder, getOrderPayment, getOrderPaymentLimits, PaymentAsset, PaymentCalculation, PaymentLimits, PaymentNetwork, PaymentStatus, UserProfile } from '../api/api';
import { useApp } from '../context/AppContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { clientErrorMessage } from '../utils/clientError';
import { useKycAutoRefresh } from '../hooks/useKycAutoRefresh';

const featureStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const dotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: 'var(--accent)',
  flexShrink: 0,
  boxShadow: '0 0 8px var(--accent)',
};

const featureTextStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: 'var(--text-muted)',
};

const MIN_PAYMENT_RUB = 10000;
const turnoverFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const ETH_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;

export interface PaymentProviderOption {
  asset: PaymentAsset;
  network: PaymentNetwork;
  label: string;
  subtitle: string;
}

export const PAYMENT_PROVIDERS: PaymentProviderOption[] = [
  { asset: 'ETH', network: 'ETHEREUM', label: 'ETH', subtitle: 'Ethereum' },
  { asset: 'USDT', network: 'BEP20', label: 'USDT · BEP20', subtitle: 'BNB Smart Chain' },
];

function formatAsset(value: number, asset: PaymentAsset) {
  return `${value.toFixed(6)} ${asset}`;
}

function formatCalculatedAsset(calculation: PaymentCalculation, asset: PaymentAsset) {
  return formatAsset(asset === 'ETH' ? calculation.assetAmount ?? calculation.usdtAmount : calculation.usdtAmount, asset);
}

function orderAsset(order: ApiOrder): PaymentAsset {
  return order.asset === 'USDT' ? 'USDT' : 'ETH';
}

export function TurnoverInfo({ turnover }: { turnover: number | null }) {
  return (
    <div className="dashboard-liquidity" aria-live="polite">
      <span className="dashboard-liquidity-label">Весь оборот</span>
      <span className="dashboard-liquidity-value mono">
        {turnover == null ? '...' : `${turnoverFormatter.format(Math.max(0, turnover))} ETH`}
      </span>
    </div>
  );
}

export function PaymentProviderSelector({
  value,
  onChange,
}: {
  value: PaymentProviderOption;
  onChange: (provider: PaymentProviderOption) => void;
}) {
  return (
    <fieldset className="network-selector">
      <legend>Провайдер получения</legend>
      <div className="network-selector-options">
        {PAYMENT_PROVIDERS.map(provider => (
          <label
            key={`${provider.asset}-${provider.network}`}
            className={`network-option${value.asset === provider.asset && value.network === provider.network ? ' network-option--active' : ''}`}
          >
            <input
              type="radio"
              name="payment-provider"
              value={`${provider.asset}-${provider.network}`}
              checked={value.asset === provider.asset && value.network === provider.network}
              onChange={() => onChange(provider)}
            />
            <span>{provider.label}</span>
            <small>{provider.subtitle}</small>
          </label>
        ))}
      </div>
      <p className="network-selector-hint">
        Выберите тот же актив и сеть в кошельке получателя. Переводы между разными сетями не зачисляются.
      </p>
    </fieldset>
  );
}

function hasProfileQuestionnaire(user: UserProfile): boolean {
  return Boolean(
    user.lastName?.trim() &&
    user.firstName?.trim() &&
    user.birthDate?.trim() &&
    user.phone?.trim()
  );
}

function qrImageSource(value: string | null | undefined): string | null {
  const source = value?.trim();
  if (!source) return null;
  if (/^(data:image\/|https?:\/\/)/i.test(source)) return source;
  if (/^(iVBOR|\/9j\/|UklGR)/.test(source)) {
    return `data:image/png;base64,${source.replace(/\s/g, '')}`;
  }
  return null;
}

export function PaymentScreen({ order, onReset }: { order: ApiOrder; onReset: () => void }) {
  const [current, setCurrent] = useState(order);
  const [payment, setPayment] = useState<PaymentStatus | null>(null);
  const [failedQrImage, setFailedQrImage] = useState<string | null>(null);
  const { userAuth } = useApp();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const poll = async () => {
      const p = await getOrderPayment(order.id);
      setPayment(p);
      if (userAuth.token) setCurrent(await getOrder(order.id, userAuth.token));
      if (['Paid', 'Fulfilled', 'Expired', 'Cancelled', 'Failed', 'completed', 'failed', 'expired'].includes(p.status)) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    };
    poll().catch(() => {});
    intervalRef.current = setInterval(() => poll().catch(() => {}), 3000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [order.id, userAuth.token]);

  const paymentLink = payment?.deepLink || current.paymentDeepLink;
  const sbpQr = payment?.sbpQr || current.paymentSbpQr;
  const sbpQrImage = qrImageSource(sbpQr);
  const qrValue = paymentLink || (sbpQrImage ? null : sbpQr);
  const qrImage = sbpQrImage || payment?.qrLink || current.paymentQrUrl;
  const canShowQrImage = Boolean(qrImage && failedQrImage !== qrImage);
  const openPaymentLink = paymentLink || payment?.qrLink || current.paymentQrUrl;
  const hasQr = Boolean(qrValue || canShowQrImage);

  return (
    <div className="landing-success">
      <h2 className="success-title">Оплатите заявку</h2>
      {hasQr ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 24 }}>
          <div className="qr-wrapper">
            {qrValue ? (
              <QRCodeSVG value={qrValue} size={200} level="M" title="QR для оплаты" />
            ) : (
              <img
                src={qrImage!}
                alt="QR для оплаты"
                width={200}
                height={200}
                onError={() => setFailedQrImage(qrImage!)}
              />
            )}
          </div>
          {openPaymentLink && (
            <a href={openPaymentLink} className="back-link" target="_blank" rel="noopener noreferrer">Открыть оплату</a>
          )}
        </div>
      ) : qrImage && failedQrImage === qrImage ? (
        <div className="payment-error" style={{ marginTop: 24 }}>
          Не удалось загрузить QR. Откройте оплату по ссылке ниже.
          {openPaymentLink && (
            <a href={openPaymentLink} className="back-link" target="_blank" rel="noopener noreferrer">Открыть оплату</a>
          )}
        </div>
      ) : (
        <div className="qr-loading-ring" />
      )}
      <div className="success-details" style={{ marginTop: 16 }}>
        <div className="success-row"><span>Заявка</span><span className="mono">{current.id}</span></div>
        <div className="success-row"><span>Сумма</span><span className="mono">{current.rub.toLocaleString('ru')} ₽</span></div>
        <div className="success-row"><span>К получению</span><span className="mono accent">{formatAsset(current.usd, orderAsset(current))}</span></div>
      </div>
      <button className="back-link" onClick={onReset}>Новая заявка</button>
    </div>
  );
}

export default function DashboardPage() {
  const { userAuth, settings, refreshSettings } = useApp();
  useKycAutoRefresh();
  const [params] = useSearchParams();
  const [rub, setRub] = useState('');
  const [provider, setProvider] = useState<PaymentProviderOption>(PAYMENT_PROVIDERS[0]);
  const [walletAddress, setWalletAddress] = useState('');
  const [calculation, setCalculation] = useState<PaymentCalculation | null>(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [limits, setLimits] = useState<PaymentLimits | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState<ApiOrder | null>(null);
  const referralCode = params.get('ref') || params.get('promo');
  const rubNum = parseFloat(rub) || 0;
  const minPaymentRub = limits?.minAmount ?? MIN_PAYMENT_RUB;
  const maxPaymentRub = limits?.maxAmount;
  const availableLiquidity = settings?.liquidity ?? null;
  const normalizedWalletAddress = walletAddress.trim();
  const walletAddressValid = ETH_ADDRESS_RE.test(normalizedWalletAddress);

  useEffect(() => {
    refreshSettings({ asset: provider.asset, network: provider.network }).catch(() => {});
  }, [provider, refreshSettings]);

  useEffect(() => {
    if (!userAuth.token) {
      setLimits(null);
      return;
    }

    let cancelled = false;
    getOrderPaymentLimits(userAuth.token)
      .then(result => { if (!cancelled) setLimits(result); })
      .catch(() => { if (!cancelled) setLimits(null); });

    return () => { cancelled = true; };
  }, [userAuth.token]);

  useEffect(() => {
    if (!userAuth.token || rubNum < minPaymentRub || (maxPaymentRub != null && rubNum > maxPaymentRub)) {
      setCalculation(null);
      setCalculationLoading(false);
      return;
    }

    let cancelled = false;
    setCalculationLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const result = await calculateOrderPayment({ rub: rubNum, asset: provider.asset, network: provider.network }, userAuth.token!);
        if (!cancelled) {
          setCalculation(result);
          setError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setCalculation(null);
          setError(clientErrorMessage(err, 'Не удалось рассчитать сумму. Повторите попытку позже.'));
        }
      } finally {
        if (!cancelled) setCalculationLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [rubNum, minPaymentRub, maxPaymentRub, provider, userAuth.token]);

  if (!userAuth.token && referralCode) {
    return <Navigate to={`/register?ref=${encodeURIComponent(referralCode)}`} replace />;
  }

  if (!userAuth.token || !userAuth.user) {
    return <Card><Link to="/login" className="back-link">Войти</Link></Card>;
  }

  const user = userAuth.user;
  const questionnaireComplete = hasProfileQuestionnaire(user);
  const kycComplete = user.isSbpVerified || user.kycStatus === 'Approved';
  const hasLiquidity = availableLiquidity != null && availableLiquidity > 0;
  const calculatedAssetAmount = calculation == null
    ? null
    : provider.asset === 'ETH'
      ? calculation.assetAmount ?? calculation.usdtAmount
      : calculation.usdtAmount;
  const hasEnoughLiquidity = calculation != null &&
    availableLiquidity != null &&
    calculatedAssetAmount != null &&
    calculatedAssetAmount <= availableLiquidity;
  const canCreate = questionnaireComplete && kycComplete && walletAddressValid && rubNum >= minPaymentRub && (maxPaymentRub == null || rubNum <= maxPaymentRub) && calculation != null && !calculationLoading && hasEnoughLiquidity;
  const showCalculation = rubNum >= minPaymentRub && (maxPaymentRub == null || rubNum <= maxPaymentRub);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!userAuth.token || !canCreate) return;
    setLoading(true);
    setError('');
    try {
      setOrder(await createOrder({ rub: rubNum, destination: normalizedWalletAddress, asset: provider.asset, network: provider.network }, userAuth.token));
    } catch (err: any) {
      setError(clientErrorMessage(err, 'Не удалось создать платёж. Повторите попытку позже.'));
    } finally {
      setLoading(false);
    }
  }

  if (order) return <PaymentScreen order={order} onReset={() => { setOrder(null); setRub(''); setWalletAddress(''); }} />;

  return (
    <div className="landing-layout">
      <div className="landing-left">
        <h1 className="landing-headline">Покупка ETH<br /><span className="accent">через СБП</span></h1>
        <p className="landing-sub">Быстрая оплата в рублях.<br />Статус платежа доступен в кабинете.</p>
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Укажите адрес кошелька для получения ETH или USDT</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Пройдите KYC перед первым платежом</span></div>
          <div style={featureStyle}><span style={dotStyle}></span><span style={featureTextStyle}>Следите за заявками в личном кабинете</span></div>
        </div>
      </div>
      <div className="landing-right">
        <Card>
          <TurnoverInfo turnover={settings?.turnover ?? null} />
          <div className="dashboard-required-actions">
            <label className="dashboard-status-check">
              <input type="checkbox" checked={questionnaireComplete} readOnly aria-label="Анкета заполнена" />
              <span>Анкета заполнена</span>
            </label>
            <label className="dashboard-status-check">
              <input type="checkbox" checked={kycComplete} readOnly aria-label="KYC пройден" />
              <span>KYC пройден</span>
            </label>
          </div>
          <form onSubmit={submit}>
            <PaymentProviderSelector value={provider} onChange={setProvider} />
            <Input
              label={`Адрес ${provider.subtitle}`}
              value={walletAddress}
              onChange={e => setWalletAddress(e.target.value)}
              placeholder="0x..."
              autoComplete="off"
              required
              error={walletAddress && !walletAddressValid ? 'Введите корректный EVM-адрес' : undefined}
            />
            <Input label="Сумма в рублях" type="number" min={minPaymentRub.toString()} max={maxPaymentRub?.toString()} value={rub} onChange={e => setRub(e.target.value)} suffix="₽" required />
            {showCalculation && (
              <div className="calc-result">
                <span className="calc-label">Получите</span>
                <span className="calc-amount mono">{calculation ? formatCalculatedAsset(calculation, provider.asset) : '...'}</span>
              </div>
            )}
            {rubNum > 0 && rubNum < minPaymentRub && <div style={{ color: 'var(--text-muted)', margin: '10px 0' }}>Минимальная сумма оплаты {minPaymentRub.toLocaleString('ru')} ₽</div>}
            {maxPaymentRub != null && rubNum > maxPaymentRub && <div style={{ color: 'var(--text-muted)', margin: '10px 0' }}>Максимальная сумма оплаты {maxPaymentRub.toLocaleString('ru')} ₽</div>}
            {availableLiquidity != null && !hasLiquidity && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>Нет доступной ликвидности {provider.asset}.</div>}
            {hasLiquidity && calculation && !hasEnoughLiquidity && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>Недостаточно ликвидности {provider.asset}. Доступно {formatAsset(Math.max(0, availableLiquidity ?? 0), provider.asset)}.</div>}
            {error && <div style={{ color: 'var(--danger)', margin: '10px 0' }}>{error}</div>}
            <Button type="submit" fullWidth size="lg" loading={loading || calculationLoading} disabled={!canCreate}>Создать оплату</Button>
          </form>
        </Card>
        <div className="legal-info-block">
          Покупка ETH через СБП осуществляется с использованием инфраструктуры лицензированного поставщика услуг виртуальных активов Bitbanker KG (Кыргызская Республика). Прохождение идентификации (KYC) является обязательным условием использования сервиса.
        </div>
      </div>
    </div>
  );
}
